import {
  Card,
  Stack,
  Text,
  Title,
  Box,
  Container,
  Center,
} from '@mantine/core';
import { getDeviceStatus } from '@utils';
import { HeartPulse, WifiOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useUserDevices } from '../../../../hooks/useUserDevices';
import { useDeviceConnectionsStatus } from '../../../../hooks/useDeviceConnectionsStatus';
import { useSession } from '../../../../hooks/useSession';

export const HeartRateSkeleton = () => {
  const { id } = useParams();
  const { session: currentExperience } = useSession(id);
  const userId = currentExperience?.user_id?.toString();
  const { devices: allDevices } = useUserDevices(userId);
  const { connectionStatus } = useDeviceConnectionsStatus(allDevices);

  // Merge devices with connection status
  const devices = allDevices.map(device => {
    const clientId = device.clientId ?? device.code;
    return {
      ...device,
      status:
        connectionStatus[clientId ?? ''] ?? device.status ?? 'disconnected',
    };
  });

  const isSensorConnected =
    getDeviceStatus(devices, 'smartwatch') === 'connected';

  const { t } = useTranslation();

  if (!isSensorConnected) {
    return (
      <Container fluid h='100%' mih={400} w='100%' bg='gray.0'>
        <Center h='100%' p='xl'>
          <Card
            padding='xl'
            radius='sm'
            bg='transparent'
            maw={450}
            w='100%'
            ta='center'
          >
            <Stack align='center' gap='xl'>
              <Box bg='gray.0' display='flex'>
                <Center>
                  <WifiOff size={56} color='var(--mantine-color-gray-4)' />
                </Center>
              </Box>

              <Stack gap='md' align='center'>
                <Title order={3} c='dimmed' fw={500}>
                  {t('sensor_not_connected_title', 'Sensore non connesso')}
                </Title>
                <Text size='sm' c='dimmed' maw={350} lh={1.6}>
                  {t(
                    'sensor_not_connected_description',
                    'Il grafico delle misurazioni non è disponibile perché il sensore non risulta connesso. Verifica la connessione del dispositivo e riprova.'
                  )}
                </Text>
              </Stack>
            </Stack>
          </Card>
        </Center>
      </Container>
    );
  }

  return (
    <Container fluid h='100%' mih={400}>
      <Center h='100%' p='xl'>
        <Stack align='center' gap='xl'>
          <Box
            p='xl'
            bg='red.0'
            display='flex'
            style={{
              borderRadius: '50%',
              border: '2px solid var(--mantine-color-red-2)',
            }}
          >
            <Center>
              <Box c='red.6'>
                <HeartPulse size={32} className='heartbeat-icon' />
              </Box>
            </Center>
          </Box>
          <Stack gap='md' align='center'>
            <Title order={3} c='dark' fw={500}>
              {t('waiting_for_heart_rate', 'In attesa di dati')}
            </Title>
            <Text size='sm' c='dimmed' maw={350} lh={1.6}>
              {t(
                'waiting_for_heart_rate_description',
                'Il sensore è connesso. In attesa di ricezione battito cardiaco...'
              )}
            </Text>
          </Stack>
        </Stack>
      </Center>
    </Container>
  );
};
